import { useState, useMemo, useEffect, Fragment } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { PersonnelProject, RateSchedule, TMWorker } from "@/hooks/usePersonnelProjects";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  project: PersonnelProject;
  rates: RateSchedule[];
  workers: TMWorker[];
}

interface LineEntry {
  workerId: string;
  rateScheduleId: string;
  workerName: string;
  roleName: string;
  billRate: number;
  costRate: number;
  mon: number; tue: number; wed: number; thu: number; fri: number; sat: number; sun: number;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

function getLastFriday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day >= 5 ? day - 5 : day + 2;
  d.setDate(d.getDate() - diff);
  return d.toISOString().split("T")[0];
}

export function TimesheetEntryDialog({ open, onOpenChange, project, rates, workers }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [weekEnding, setWeekEnding] = useState(getLastFriday);
  const [saving, setSaving] = useState(false);

  const activeWorkers = workers.filter((w) => w.is_active);

  const buildLines = () =>
    activeWorkers.map((w) => {
      const rate = rates.find((r) => r.id === w.rate_schedule_id);
      return {
        workerId: w.id,
        rateScheduleId: w.rate_schedule_id,
        workerName: w.worker_name,
        roleName: rate?.role_name ?? "",
        billRate: w.bill_rate_override ?? rate?.bill_rate_regular ?? 0,
        costRate: w.cost_rate_override ?? rate?.cost_rate ?? 0,
        mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0,
      };
    });

  const [lines, setLines] = useState<LineEntry[]>(buildLines);

  useEffect(() => {
    if (open) {
      setLines(buildLines());
      setWeekEnding(getLastFriday());
    }
  }, [open]);

  const updateHours = (idx: number, day: typeof DAYS[number], val: number) => {
    setLines((l) => l.map((row, i) => (i === idx ? { ...row, [day]: val } : row)));
  };

  const getTotal = (l: LineEntry) => l.mon + l.tue + l.wed + l.thu + l.fri + l.sat + l.sun;

  // Group by role
  const grouped = useMemo(() => {
    const groups: Record<string, LineEntry[]> = {};
    lines.forEach((l) => {
      if (!groups[l.roleName]) groups[l.roleName] = [];
      groups[l.roleName].push(l);
    });
    return groups;
  }, [lines]);

  const grandTotal = lines.reduce((s, l) => s + getTotal(l), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("company_id")
        .eq("user_id", user!.id)
        .single();
      if (!profile) throw new Error("No company");

      // Create timesheet
      const { data: ts, error: tsErr } = await supabase
        .from("tm_timesheets" as any)
        .insert({
          company_id: profile.company_id,
          personnel_project_id: project.id,
          week_ending: weekEnding,
          created_by: user!.id,
        })
        .select()
        .single();
      if (tsErr) throw tsErr;

      // Create lines
      const lineInserts = lines
        .filter((l) => getTotal(l) > 0)
        .map((l) => ({
          company_id: profile.company_id,
          timesheet_id: (ts as any).id,
          personnel_project_id: project.id,
          worker_id: l.workerId,
          rate_schedule_id: l.rateScheduleId,
          mon_hours: l.mon,
          tue_hours: l.tue,
          wed_hours: l.wed,
          thu_hours: l.thu,
          fri_hours: l.fri,
          sat_hours: l.sat,
          sun_hours: l.sun,
        }));

      if (lineInserts.length > 0) {
        const { error: lineErr } = await supabase
          .from("tm_timesheet_lines" as any)
          .insert(lineInserts);
        if (lineErr) throw lineErr;
      }

      qc.invalidateQueries({ queryKey: ["tm_timesheets", project.id] });
      toast.success("Timesheet saved");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Timesheet — {project.project_number}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-4 mb-4">
          <Label>Week Ending:</Label>
          <Input type="date" className="w-48" value={weekEnding} onChange={(e) => setWeekEnding(e.target.value)} />
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[140px]">Worker</TableHead>
                {DAYS.map((d) => (
                  <TableHead key={d} className="w-16 text-center">{d.charAt(0).toUpperCase() + d.slice(1, 2)}</TableHead>
                ))}
                <TableHead className="text-right w-16">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(grouped).map(([role, roleLines]) => (
                <Fragment key={role}>
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={9} className="font-semibold text-xs uppercase tracking-wider py-1">
                      {role} — Cost: ${roleLines[0]?.costRate}/HR | Bill: ${roleLines[0]?.billRate}/HR
                    </TableCell>
                  </TableRow>
                  {roleLines.map((l) => {
                    const idx = lines.indexOf(l);
                    const total = getTotal(l);
                    return (
                      <TableRow key={l.workerId}>
                        <TableCell className="text-sm">{l.workerName}</TableCell>
                        {DAYS.map((d) => (
                          <TableCell key={d} className="p-1">
                            <Input
                              type="number"
                              className="h-8 w-14 text-center text-sm"
                              value={l[d] || ""}
                              onChange={(e) => updateHours(idx, d, parseFloat(e.target.value) || 0)}
                              min={0}
                              max={24}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-medium">{total}</TableCell>
                      </TableRow>
                    );
                  })}
                </Fragment>
              ))}
              <TableRow className="font-bold border-t-2">
                <TableCell>TOTAL</TableCell>
                <TableCell colSpan={7} />
                <TableCell className="text-right">{grandTotal}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || grandTotal === 0}>
            {saving ? "Saving…" : "Save Draft"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
